import React, { useState, useEffect } from 'react';
import { FiX, FiGitBranch, FiAlertCircle, FiRefreshCw, FiCheckCircle, FiAlertTriangle, FiStar } from 'react-icons/fi';
import { compareArchitectureVariants, getAiCache, saveAiCache, type ArchitectureVariant, type CompareVariantsResult, type AiCacheHistoryEntry } from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  architectureId?: string;
}

const COMPLEXITY_COLOR: Record<string, string> = {
  low: 'text-green-600 bg-green-50 border-green-200',
  medium: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  high: 'text-red-600 bg-red-50 border-red-200',
};

const EFFORT_LABEL: Record<string, string> = {
  low: 'Facile', medium: 'Moyen', high: 'Difficile',
};

const SCORE_COLOR = (v: number) =>
  v >= 4 ? 'bg-green-400' : v >= 3 ? 'bg-yellow-400' : 'bg-red-400';

function VariantCard({ variant, selected, onSelect }: {
  variant: ArchitectureVariant;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
        selected ? 'border-purple-500 bg-purple-50' : 'border-gray-100 hover:border-purple-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{variant.name}</h4>
          <p className="text-xs text-purple-600 font-medium">{variant.tagline}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${COMPLEXITY_COLOR[variant.complexity]}`}>
          {variant.complexity}
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3 leading-relaxed">{variant.description}</p>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="font-semibold text-gray-700">{variant.estimatedMonthlyCost}</span>
        <span>Migration : {EFFORT_LABEL[variant.migrationEffort]}</span>
      </div>
    </div>
  );
}

function VariantDetail({ variant }: { variant: ArchitectureVariant }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">{variant.name}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{variant.description}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 bg-green-50 border border-green-100 rounded-xl">
          <h5 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1.5">
            <FiCheckCircle size={12} /> Avantages
          </h5>
          <ul className="space-y-1">
            {variant.pros.map((p, i) => (
              <li key={i} className="text-xs text-green-800 flex items-start gap-1.5">
                <span className="text-green-500 mt-0.5">✓</span> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
          <h5 className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5">
            <FiAlertTriangle size={12} /> Inconvénients
          </h5>
          <ul className="space-y-1">
            {variant.cons.map((c, i) => (
              <li key={i} className="text-xs text-red-800 flex items-start gap-1.5">
                <span className="text-red-400 mt-0.5">✗</span> {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {variant.keyDifferences?.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
          <h5 className="text-xs font-semibold text-blue-700 mb-2">Différences clés vs architecture actuelle</h5>
          <ul className="space-y-1">
            {variant.keyDifferences.map((d, i) => (
              <li key={i} className="text-xs text-blue-800 flex items-start gap-1.5">
                <span className="text-blue-500 mt-0.5">→</span> {d}
              </li>
            ))}
          </ul>
        </div>
      )}

      {variant.resources?.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-600 mb-2">Ressources principales</h5>
          <div className="space-y-1">
            {variant.resources.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{r.type}</span>
                <span className="font-medium">{r.label}</span>
                <span className="text-gray-400">— {r.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-600">
        <span className="font-semibold">Idéal pour : </span>{variant.bestFor}
      </div>
    </div>
  );
}

const CompareVariantsModal: React.FC<Props> = ({ isOpen, onClose, nodes, edges, provider, environment, architectureName, architectureId }) => {
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [result, setResult]         = useState<CompareVariantsResult | null>(null);
  const [selected, setSelected]     = useState(0);
  const [metadata, setMetadata]     = useState<{ durationMs: number; tokensUsed: number } | null>(null);
  const [fromCache, setFromCache]   = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) return;
    setResult(null); setError(null); setSelected(0); setFromCache(false);
    setCacheUpdatedAt(undefined);
    loadOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadOrGenerate = async () => {
    if (architectureId) {
      try {
        const cached = await getAiCache(architectureId, 'variants');
        setResult(cached.content);
        setMetadata(cached.content.metadata ? { durationMs: cached.content.metadata.durationMs, tokensUsed: cached.content.metadata.tokensUsed } : null);
        setFromCache(true);
        setCacheUpdatedAt(cached.updatedAt);
        return;
      } catch {
        // pas de cache, générer
      }
    }
    generate();
  };

  const generate = async () => {
    setLoading(true); setError(null); setFromCache(false); setCacheUpdatedAt(undefined);
    try {
      const r = await compareArchitectureVariants({ nodes, edges, provider, environment, architectureName, variantsCount: 3 });
      setResult(r);
      setMetadata({ durationMs: r.metadata.durationMs, tokensUsed: r.metadata.tokensUsed });
      if (architectureId) {
        saveAiCache(architectureId, 'variants', r).catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (entry: AiCacheHistoryEntry) => {
    setResult(entry.content);
    setMetadata(entry.content.metadata ? { durationMs: entry.content.metadata.durationMs, tokensUsed: entry.content.metadata.tokensUsed } : null);
    setFromCache(true);
    setCacheUpdatedAt(entry.savedAt);
    setSelected(0);
  };

  const handleExportPdf = () => {
    if (!result) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Variantes — ${architectureName || 'Architecture'}</title>
    <style>body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111}
    h1{font-size:20px;font-weight:700}h2{font-size:16px;font-weight:600;margin:20px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
    h3{font-size:14px;font-weight:600;margin:12px 0 4px}.variant{border:1px solid #e5e7eb;border-radius:8px;padding:16px;margin:10px 0;page-break-inside:avoid}
    .tag{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;border:1px solid}
    .low{color:#16a34a;border-color:#bbf7d0;background:#f0fdf4}.medium{color:#b45309;border-color:#fde68a;background:#fffbeb}.high{color:#dc2626;border-color:#fca5a5;background:#fef2f2}
    table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #e5e7eb;padding:8px;text-align:left;font-size:12px}th{background:#f9fafb;font-weight:600}
    ul{margin:6px 0;padding-left:18px}li{font-size:13px;margin:2px 0}
    @media print{body{padding:16px}}</style></head><body>`);
    w.document.write(`<h1>Variantes d'Architecture — ${architectureName || ''}</h1>`);
    if (result.recommendation) {
      w.document.write(`<div style="background:#fefce8;border:1px solid #fde68a;border-radius:8px;padding:12px 16px;margin:12px 0;font-size:13px"><strong>Recommandation : </strong>${result.recommendation}</div>`);
    }
    result.variants.forEach(v => {
      w.document.write(`<div class="variant"><h2>${v.name} <span class="tag ${v.complexity}">${v.complexity}</span></h2><p style="color:#6d28d9;font-size:13px;font-weight:500">${v.tagline}</p><p style="font-size:13px">${v.description}</p><p style="font-size:13px"><strong>Coût estimé : </strong>${v.estimatedMonthlyCost} · <strong>Migration : </strong>${v.migrationEffort}</p>`);
      if (v.pros?.length) { w.document.write('<h3>Avantages</h3><ul>'); v.pros.forEach(p => w.document.write(`<li>${p}</li>`)); w.document.write('</ul>'); }
      if (v.cons?.length) { w.document.write('<h3>Inconvénients</h3><ul>'); v.cons.forEach(c => w.document.write(`<li>${c}</li>`)); w.document.write('</ul>'); }
      w.document.write('</div>');
    });
    if (result.comparison?.dimensions && result.comparison.matrix) {
      w.document.write('<h2>Matrice de comparaison</h2><table><tr><th>Dimension</th>');
      result.variants.forEach(v => w.document.write(`<th>${v.name}</th>`));
      w.document.write('</tr>');
      result.comparison.dimensions.forEach(dim => {
        w.document.write(`<tr><td>${dim}</td>`);
        result.variants.forEach(v => w.document.write(`<td>${result.comparison!.matrix[v.id]?.[dim] ?? 0}/5</td>`));
        w.document.write('</tr>');
      });
      w.document.write('</table>');
    }
    w.document.write('</body></html>');
    w.document.close();
    w.print();
  };

  if (!isOpen) return null;
  const currentVariant = result?.variants[selected];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <FiGitBranch size={18} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Variantes d'Architecture</h2>
              {architectureName && <p className="text-xs text-gray-500">{architectureName}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {result && (
              <button onClick={generate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                <FiRefreshCw size={12} /> Regénérer
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><FiX size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Génération de 3 variantes…</p>
            </div>
          )}

          {error && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <FiAlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-red-700">Erreur</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <button onClick={generate} className="mt-2 text-xs text-red-700 underline">Réessayer</button>
              </div>
            </div>
          )}

          {result && (
            <div className="p-6 space-y-6">
              {/* Recommandation globale */}
              {result.recommendation && (
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <FiStar size={16} className="text-yellow-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800"><span className="font-semibold">Recommandation : </span>{result.recommendation}</p>
                </div>
              )}

              {/* Grille des variantes */}
              <div className="grid grid-cols-3 gap-3">
                {result.variants.map((v, i) => (
                  <VariantCard key={v.id} variant={v} selected={selected === i} onSelect={() => setSelected(i)} />
                ))}
              </div>

              {/* Matrice de comparaison */}
              {result.comparison?.dimensions && result.comparison.matrix && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Matrice de comparaison</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-2 text-gray-600 font-medium border border-gray-100">Dimension</th>
                          {result.variants.map(v => (
                            <th key={v.id} className="text-center p-2 text-gray-700 font-semibold border border-gray-100">{v.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.comparison.dimensions.map(dim => (
                          <tr key={dim} className="hover:bg-gray-50">
                            <td className="p-2 text-gray-600 border border-gray-100">{dim}</td>
                            {result.variants.map(v => {
                              const score = result.comparison!.matrix[v.id]?.[dim] ?? 0;
                              return (
                                <td key={v.id} className="p-2 border border-gray-100">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="flex gap-0.5">
                                      {[1, 2, 3, 4, 5].map(n => (
                                        <div key={n} className={`w-3 h-3 rounded-sm ${n <= score ? SCORE_COLOR(score) : 'bg-gray-100'}`} />
                                      ))}
                                    </div>
                                    <span className="text-gray-500 ml-1">{score}/5</span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Détail de la variante sélectionnée */}
              {currentVariant && (
                <div className="border border-purple-100 rounded-2xl p-5 bg-purple-50/30">
                  <h3 className="text-sm font-semibold text-purple-800 mb-4">
                    Détail — {currentVariant.name}
                  </h3>
                  <VariantDetail variant={currentVariant} />
                </div>
              )}
            </div>
          )}
        </div>

        {(metadata || result) && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 shrink-0">
            <AiCacheActions
              architectureId={architectureId}
              cacheType="variants"
              content={result}
              fromCache={fromCache}
              updatedAt={cacheUpdatedAt}
              onRestore={handleRestore}
              onExportPdf={handleExportPdf}
            />
            <div className="flex items-center gap-4">
              {metadata && <><span>{metadata.tokensUsed.toLocaleString()} tokens</span><span>{(metadata.durationMs / 1000).toFixed(1)}s</span></>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareVariantsModal;
